import { useState } from 'react';
import { GRAY_400, type Message } from './App';
import { ResumeLink } from './components/ResumeLink';
import { EmailLink } from './components/EmailLink';
import { SmartLink } from './components/SmartLinks';

export const useDefaultMessages: (sendPrompt: (_: string) => Promise<string> | void) => Message[] = (
  sendPrompt
) => [
  {
    type: 'prompt',
    content: 'Tell me about Vincent Liaw',
  },
  {
    type: 'response',
    content: (
      <>
        <p>
          Fullstack engineer focused on simplifying complex systems and building
          scalable products, from UX to systems.
        </p>
        <p>
          At <SmartLink href="https://www.databricks.com">Databricks</SmartLink>, building out the ingestion UX across multiple
          products — consolidating fragmented workflows, scaling to many data
          sources, and streamlining work across teams.
        </p>
        <p>
          Also built{' '}
          <SmartLink href="https://cardclues.com">CardClues</SmartLink>
          , an AI-powered tool for optimizing credit card spend.
        </p>
        <ul>
          <li>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ verticalAlign: '-2px', marginRight: 6 }}
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
              <polyline points="10 9 9 9 8 9" />
            </svg>
            <ResumeLink />
          </li>
          <li>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ verticalAlign: '-2px', marginRight: 6 }}
            >
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
              <polyline points="22,6 12,13 2,6" />
            </svg>
            <EmailLink text="vincentcliaw@gmail.com" /> (<CopyButton />)
          </li>
          <li>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="currentColor"
              style={{ verticalAlign: '-2px', marginRight: 4 }}
            >
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
            </svg>
            <a
              href="https://www.linkedin.com/in/vincentliaw"
              target="_blank"
              rel="noopener noreferrer"
            >
              LinkedIn
            </a>{' '}
            /{' '}
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="currentColor"
              style={{ verticalAlign: '-2px', marginRight: 4, marginLeft: 4 }}
            >
              <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
            </svg>
            <a
              href="https://github.com/liawesomesaucer"
              target="_blank"
              rel="noopener noreferrer"
            >
              GitHub
            </a>{' '}
          </li>
        </ul>
        <p>
          <i>You can ask me:</i>
        </p>

        <PromptButton
          prompt={'What did Vincent do at Databricks?'}
          sendPrompt={sendPrompt}
        />
        <PromptButton
          prompt={'What is CardClues?'}
          sendPrompt={sendPrompt}
        />
        <PromptButton
          prompt={'Tell me about some of Vincent\'s work'}
          sendPrompt={sendPrompt}
        />
      </>
    ),
  },
];

const ReturnArrow = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: '-1px', marginRight: 6 }}>
    <polyline points="15 10 20 15 15 20" />
    <path d="M4 4v7a4 4 0 0 0 4 4h12" />
  </svg>
);

const PromptButton = ({
  prompt,
  sendPrompt,
}: {
  prompt: string;
  sendPrompt: (_: string) => Promise<string> | void;
}) => {
  const [msgId, setMsgId] = useState<string | null>(null);
  const sent = Boolean(msgId);

  const handleSend = async (e: React.MouseEvent) => {
    e.preventDefault();
    const id = await sendPrompt(prompt);
    if (id) setMsgId(id);
  };

  const handleScroll = (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById(msgId!)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <p style={{ margin: '4px 0' }}>
      {sent ? <span style={{ marginRight: 6 }}>&#10003;</span> : <ReturnArrow />}
      <a
        href="#"
        style={{ cursor: 'pointer' }}
        onClick={sent ? handleScroll : handleSend}
      >
        {prompt}
      </a>
    </p>
  );
};

const CopyButton = () => {
  const [copied, setCopied] = useState(false);

  return (
    <span
      style={{
        color: 'var(--link)',
        cursor: 'pointer',
        borderBottom: '1px solid',
        fontWeight: 'bold',
      }}
      tabIndex={0}
      onClick={async () => {
        await navigator.clipboard.writeText('vincentcliaw@gmail.com');
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
    >
      {copied ? 'copied!' : 'copy'}
    </span>
  );
};
